/**
 * FAQ Page Component
 * 
 * Frequently Asked Questions about WordExpress theme generation
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const FAQSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  cursor: 'pointer',
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:before': {
    display: 'none',
  },
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const FAQ = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const categories = [
    { id: 'all', label: 'All Questions' },
    { id: 'general', label: 'General' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'technical', label: 'Technical' },
    { id: 'customization', label: 'Customization' },
  ];

  const faqs = [
    {
      category: 'general',
      question: 'What is WordExpress?',
      answer: 'WordExpress builds WordPress themes around your business. You choose your industry, pick the pages you need, and we put together a complete, installable theme — ready to upload to WordPress. No coding, no drag-and-drop builders, no generic templates from 2015.'
    },
    {
      category: 'general',
      question: 'How long does it take to generate a theme?',
      answer: 'Quick Themes are delivered instantly (if cached) or within 5-10 minutes on first generation. Custom themes take approximately 10-15 minutes to generate. You\'ll see real-time progress updates as your theme comes together.'
    },
    {
      category: 'general',
      question: 'Do I need coding skills to use WordExpress?',
      answer: 'No! WordExpress is designed for non-technical users. Simply choose your preferences through our visual interface, and we\'ll generate clean, ready-to-install WordPress code for you. Installation is as easy as uploading a ZIP file to WordPress.'
    },
    {
      category: 'general',
      question: 'What industries do you support?',
      answer: 'We support 10 major industries with 45+ specializations: Healthcare (dentists, doctors, veterinarians), Legal (family law, corporate, criminal), Construction (residential, commercial, landscaping), Restaurants (fine dining, fast casual, cafes), E-commerce, Professional Services, Real Estate, Education, Technology, and Creative Services.'
    },
    {
      category: 'pricing',
      question: 'What does "pay-on-completion" mean?',
      answer: 'You\'re only charged if your theme generates successfully and passes our validation checks. If generation fails for any reason, you pay $0 and get up to 3 free retries. This protects you from paying for incomplete or broken themes.'
    },
    {
      category: 'pricing',
      question: 'Quick vs Custom — which should I choose?',
      answer: 'Quick Theme ($49) is template-based — choose a style, pick colors and fonts, and your theme is ready in 5–10 minutes. It\'s great for portfolios, small business sites, and fast turnarounds. Custom Theme ($149) is built for your specific industry — you select from up to 70 industry-specific pages, and the content is written to fit what you do. Takes 15–20 minutes. If you\'re not sure, start with Quick.'
    },
    {
      category: 'pricing',
      question: 'Do you offer refunds?',
      answer: 'We only charge for successful themes, so refunds are rarely needed. If you have an issue with a delivered theme, contact support within 7 days and we\'ll make it right.'
    },
    {
      category: 'technical',
      question: 'Will my theme work with the latest WordPress version?',
      answer: 'Yes! All generated themes are compatible with the latest WordPress version and follow WordPress coding standards. Themes are tested for compatibility, security, and performance before delivery.'
    },
    {
      category: 'technical',
      question: 'Can I use my theme with page builders like Elementor or Gutenberg?',
      answer: 'Absolutely! Our themes are built to work seamlessly with popular page builders including Gutenberg (WordPress native), Elementor, WPBakery, and others. You can further customize your theme using any page builder you prefer.'
    },
    {
      category: 'technical',
      question: 'Are the themes SEO-friendly?',
      answer: 'Yes! All themes include clean, semantic HTML, proper heading structure, schema markup, fast loading times, and mobile responsiveness - all essential for good SEO. We follow WordPress and Google best practices.'
    },
    {
      category: 'technical',
      question: 'Do themes include responsive design?',
      answer: 'All themes are fully responsive and mobile-optimized. Your site will look great on desktops, tablets, and smartphones. We test on multiple devices and screen sizes before delivery.'
    },
    {
      category: 'customization',
      question: 'Can I customize my theme after generation?',
      answer: 'Absolutely! Generated themes are fully editable. You can modify them using WordPress Customizer, page builders, or by editing the code directly. You have complete control and ownership of your theme files.'
    },
    {
      category: 'customization',
      question: 'What pages are included in a theme?',
      answer: 'Quick Themes include 7 standard pages (Home, About, Services, Contact, Blog, Privacy, Terms). Custom themes let you choose exactly which pages you need from our extensive library, including industry-specific pages like Appointment Booking for healthcare or Case Studies for legal.'
    },
    {
      category: 'customization',
      question: 'Can I change colors and fonts after generation?',
      answer: 'Yes! While we generate themes with your chosen colors and typography, you can easily modify them later using WordPress Customizer or CSS. All themes use CSS variables for easy color scheme updates.'
    },
    {
      category: 'technical',
      question: 'How do I install my generated theme?',
      answer: 'Installation is simple: 1) Download your theme ZIP file, 2) Go to WordPress Admin → Appearance → Themes → Add New, 3) Upload the ZIP file, 4) Activate. That\'s it! Your theme is live. We also provide installation instructions with every theme.'
    },
    {
      category: 'general',
      question: 'What makes WordExpress different from other theme generators?',
      answer: 'Three things: 1) You only pay when your theme is ready — no charge for failed generations, 2) Themes are built around your industry, not one-size-fits-all, 3) You get a real installable WordPress theme, not a page builder template you have to rebuild from scratch.'
    },
    {
      category: 'technical',
      question: 'Do you provide support after generation?',
      answer: 'Yes — all users get documentation and email support. We help with installation issues, theme questions, and customization guidance. Reach us at support@wordexpress.com.'
    },
    {
      category: 'customization',
      question: 'Can I request specific features or functionality?',
      answer: 'The Custom Theme builder lets you specify a lot during generation — pages, industry, content, and more. For highly specialized needs beyond our options, reach out via the contact form and we\'ll see what we can do.'
    }
  ];

  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Everything you need to know about WordExpress theme generation
          </Typography>
        </Container>
      </Box>

      {/* FAQ Content */}
      <FAQSection>
        <Container maxWidth="md">
          {/* Category Filter */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Filter by category:
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
              {categories.map((cat) => (
                <CategoryChip
                  key={cat.id}
                  label={cat.label}
                  color={selectedCategory === cat.id ? 'primary' : 'default'}
                  onClick={() => setSelectedCategory(cat.id)}
                />
              ))}
            </Stack>
          </Box>

          {/* FAQ Accordions */}
          {filteredFAQs.map((faq, index) => (
            <StyledAccordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}bh-content`}
                id={`panel${index}bh-header`}
              >
                <Typography variant="h6" fontWeight="600">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="textSecondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </StyledAccordion>
          ))}

          {/* Still Have Questions */}
          <Box sx={{ mt: 6, p: 4, backgroundColor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Still have questions?
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Can't find the answer you're looking for? Our support team is here to help.
            </Typography>
            <Typography variant="body1">
              Email us at: <strong>support@wordexpress.com</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              We typically respond within 24 hours.
            </Typography>
          </Box>
        </Container>
      </FAQSection>
    </Box>
  );
};

export default FAQ;
